/**
 * The `initialData` the client-facing `PublicOutgoingIntake` takes in
 * `mode="preview"`: the intake as the client would receive it, with the
 * firm's branding and the attorney's name. Shared by the preview pane (no
 * submission) and the review pane (the client's answers, review mode).
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";

import type {
  OutgoingIntake,
  OutgoingIntakeFormNode,
  OutgoingIntakeSubmission
} from "@/api/types/outgoingIntake";
import { normalizeAvatarSrc } from "@/helpers/userDisplay";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import { useRequestsEnv } from "../context/RequestsEnvContext";

export const usePreviewInitialData = (
  intake: OutgoingIntake | null,
  schema: OutgoingIntakeFormNode[],
  submission: OutgoingIntakeSubmission | null,
  opts?: {
    title?: string;
    enableFirmBranding?: boolean | null;
    backgroundColor?: string | null;
    /** The note the client reads at the top of the form (`send_intake`'s `note`). */
    attorneyNote?: string | null;
    /** For when there is no `RequestsEnvProvider` (the Case IDE chat). */
    clientName?: string | null;
  }
) => {
  const env = useRequestsEnv();
  const attorneyName = useSelector(lawyerSelectors.selectFullName);
  const avatarSrc = useSelector(lawyerSelectors.selectAvatarSrc);
  return useMemo(() => {
    if (!intake) return undefined;
    const clientName = opts?.clientName ?? env.clientName ?? "";
    const firstName = clientName.trim().split(/\s+/)[0] || null;
    return {
      outgoing_intake: {
        outgoing_intake_id: String(intake.outgoing_intake_id),
        title: String(opts?.title ?? intake.title ?? "Intake"),
        status: "sent",
        schema,
        latest_submission_id: null,
        law_firm_name: env.lawFirmName ?? intake.practice_name ?? null,
        law_firm_logo_url:
          env.lawFirmLogoUrl ?? intake.law_firm_logo_url ?? null,
        enable_whitelabel_foreign_national_intake:
          env.enableWhitelabelForeignNationalIntake === true,
        enable_firm_branding:
          opts?.enableFirmBranding !== undefined
            ? opts.enableFirmBranding
            : intake.enable_firm_branding ?? null,
        intake_background_color:
          opts?.backgroundColor !== undefined
            ? opts.backgroundColor
            : intake.intake_background_color ?? null,
        foreign_national_first_name: firstName,
        attorney_name: (attorneyName || "").trim() || "Your attorney",
        attorney_avatar_url: normalizeAvatarSrc(avatarSrc) || null,
        attorney_note: opts?.attorneyNote?.trim() || null,
        welcome_video_url: intake.welcome_video_url ?? null
      },
      latest_submission: submission
        ? { ...submission, review_status: "accepted" as const, decisions: [] }
        : null
    };
  }, [
    attorneyName,
    avatarSrc,
    env,
    intake,
    opts?.attorneyNote,
    opts?.backgroundColor,
    opts?.clientName,
    opts?.enableFirmBranding,
    opts?.title,
    schema,
    submission
  ]);
};
